/* eslint-disable */
import React, { useCallback, useEffect, useState, useRef } from "react";

const UploadEverywhere = ({
    onFileUpload,
    acceptedFileTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'],
    maxFileSize = 50 * 1024 * 1024, // 50MB default
    disabled = false,
    multiple = true,
    overlayContent,
    overlayTitle,
    overlayDescription,
    isUploading = false,
    overlayClassName = "",
    overlayStyle = {},
    onDragStart,
    onDragEnd,
    validateFiles,
    overlayMode = false,
}) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const dragOverRef = useRef(false);

    // Add CSS to ensure full coverage in iframe
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .upload-everywhere-overlay {
                position: fixed !important;
                top: 0 !important;
                left: 0 !important;
                right: 0 !important;
                bottom: 0 !important;
                width: 100vw !important;
                height: 100vh !important;
                min-width: 100vw !important;
                min-height: 100vh !important;
                max-width: none !important;
                max-height: none !important;
                z-index: 999999 !important;
                box-sizing: border-box !important;
                margin: 0 !important;
                padding: 0 !important;
            }
            
            .upload-everywhere-overlay.overlay-mode {
                width: 100vw !important;
                min-width: 100vw !important;
                max-width: 100vw !important;
                left: 50% !important;
                transform: translateX(-50%) !important;
                right: auto !important;
            }
            
            @media screen {
                .upload-everywhere-overlay:not(.overlay-mode) {
                    width: 100% !important;
                    height: 100% !important;
                    min-width: 100% !important;
                    min-height: 100% !important;
                }
            }
        `;
        document.head.appendChild(style);
        
        return () => {
            if (document.head.contains(style)) {
                document.head.removeChild(style);
            }
        };
    }, []);

    // Debug dimensions when overlay shows
    useEffect(() => {
        if (isDragOver) {
            console.log('UploadEverywhere overlay dimensions:', {
                windowWidth: window.innerWidth,
                windowHeight: window.innerHeight,
                documentWidth: document.documentElement.clientWidth,
                documentHeight: document.documentElement.clientHeight,
                bodyWidth: document.body.clientWidth,
                bodyHeight: document.body.clientHeight,
                isInIframe: window !== window.parent
            });
        }
    }, [isDragOver]);

    const getFileTypeDescription = useCallback(() => {
        if (acceptedFileTypes.includes('application/pdf') && acceptedFileTypes.length === 1) {
            return 'PDF files only';
        }
        if (acceptedFileTypes.includes('image/jpeg') || acceptedFileTypes.includes('image/png')) {
            const hasXml = acceptedFileTypes.some(type => type.includes('xml'));
            return hasXml ? 'Images, documents, and XML files' : 'Images and documents';
        }
        return 'Accepted file types';
    }, [acceptedFileTypes]);

    const getFileSizeDescription = useCallback(() => {
        const sizeMB = Math.round(maxFileSize / (1024 * 1024));
        return `up to ${sizeMB}MB each`;
    }, [maxFileSize]);

    const validateDroppedFiles = useCallback((files) => {
        // Filter by accepted file types
        const typeValidFiles = files.filter(file => 
            acceptedFileTypes.includes(file.type)
        );
        
        // Filter by file size
        const validFiles = typeValidFiles.filter(file => file.size <= maxFileSize);
        const oversizedFiles = typeValidFiles.filter(file => file.size > maxFileSize);
        
        // Files with invalid types
        const invalidTypeFiles = files.filter(file => 
            !acceptedFileTypes.includes(file.type)
        );

        const errors = [];
        
        if (invalidTypeFiles.length > 0) {
            errors.push(`${invalidTypeFiles.length} file(s) have unsupported file types and will be skipped`);
        }
        
        if (oversizedFiles.length > 0) {
            const sizeMB = Math.round(maxFileSize / (1024 * 1024));
            errors.push(`${oversizedFiles.length} file(s) exceed ${sizeMB}MB limit and will be skipped`);
        }

        // Log warnings for invalid files
        errors.forEach(error => console.warn(error));

        return {
            valid: validFiles,
            invalid: [...invalidTypeFiles, ...oversizedFiles],
            errors
        };
    }, [acceptedFileTypes, maxFileSize]);

    const handleFileUpload = useCallback(async (files) => {
        if (files.length === 0) return;

        // Use custom validation if provided, otherwise use default
        const validation = validateFiles ? validateFiles(files) : validateDroppedFiles(files);
        
        if (validation.valid.length > 0) {
            // Handle multiple files based on the multiple prop
            const filesToUpload = multiple ? validation.valid : [validation.valid[0]];
            
            try {
                await onFileUpload(filesToUpload);
            } catch (error) {
                console.error('Error uploading files:', error);
            }
        }
    }, [onFileUpload, validateFiles, validateDroppedFiles, multiple]);

    useEffect(() => {
        let hideTimeout = null;
        const isInIframe = window !== window.parent;

        const showOverlay = () => {
            if (!disabled && !isUploading && !dragOverRef.current) {
                dragOverRef.current = true;
                setIsDragOver(true);
                onDragStart?.();
            }
        };

        const hideOverlay = () => {
            if (dragOverRef.current) {
                dragOverRef.current = false;
                setIsDragOver(false);
                onDragEnd?.();
            }
        };

        const scheduleHide = () => {
            if (hideTimeout) {
                clearTimeout(hideTimeout);
            }
            hideTimeout = setTimeout(hideOverlay, 200);
        };

        const cancelHide = () => {
            if (hideTimeout) {
                clearTimeout(hideTimeout);
                hideTimeout = null;
            }
        };

        const handleDragEnter = (e) => {
            e.preventDefault();
            if (e.dataTransfer?.types.includes('Files')) {
                cancelHide();
                showOverlay();
            }
        };

        const handleDragOver = (e) => {
            e.preventDefault();
            if (e.dataTransfer?.types.includes('Files')) {
                cancelHide();
                showOverlay();
            }
        };

        const handleDragLeave = (e) => {
            e.preventDefault();
            // For iframe, be more aggressive about detecting when we leave
            if (isInIframe) {
                // In iframe, schedule hide on any drag leave
                scheduleHide();
            } else {
                // Only schedule hide if we're leaving the document entirely
                if (!e.relatedTarget || e.relatedTarget === document.documentElement) {
                    scheduleHide();
                }
            }
        };

        const handleDrop = async (e) => {
            e.preventDefault();
            cancelHide();
            hideOverlay();

            if (disabled || isUploading) {
                return;
            }

            const files = Array.from(e.dataTransfer?.files || []);
            if (files.length > 0) {
                await handleFileUpload(files);
            }
        };

        // For iframe, also listen to parent window events
        let parentMessageListener = null;
        if (isInIframe) {
            parentMessageListener = (event) => {
                const { type, data } = event.data || {};
                
                switch (type) {
                    case 'PARENT_DRAG_ENTER':
                        if (data?.hasFiles && !disabled && !isUploading) {
                            cancelHide();
                            showOverlay();
                        }
                        break;
                    case 'PARENT_DRAG_LEAVE':
                        scheduleHide();
                        break;
                    case 'PARENT_DROP':
                        if (data?.files && !disabled && !isUploading) {
                            cancelHide();
                            hideOverlay();
                            try {
                                // Convert serialized file data back to File objects
                                const files = data.files.map(fileData => {
                                    const uint8Array = new Uint8Array(fileData.arrayBuffer);
                                    return new File([uint8Array], fileData.name, {
                                        type: fileData.type,
                                        lastModified: fileData.lastModified
                                    });
                                });
                                handleFileUpload(files);
                            } catch (error) {
                                console.error('Error reconstructing files from parent:', error);
                            }
                        }
                        break;
                }
            };
            
            window.addEventListener('message', parentMessageListener);
        }

        // Add event listeners to document
        document.addEventListener('dragenter', handleDragEnter);
        document.addEventListener('dragover', handleDragOver);
        document.addEventListener('dragleave', handleDragLeave);
        document.addEventListener('drop', handleDrop);

        return () => {
            if (hideTimeout) {
                clearTimeout(hideTimeout);
            }
            if (parentMessageListener) {
                window.removeEventListener('message', parentMessageListener);
            }
            document.removeEventListener('dragenter', handleDragEnter);
            document.removeEventListener('dragover', handleDragOver);
            document.removeEventListener('dragleave', handleDragLeave);
            document.removeEventListener('drop', handleDrop);
        };
    }, [disabled, isUploading, handleFileUpload, onDragStart, onDragEnd]);

    // Default overlay content
    const defaultOverlayContent = (
        <div style={{
            backgroundColor: 'var(--surface-card)',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            border: '1px solid var(--primary-color)',
        }}>
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px'
            }}>
                <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                }}>
                    {/* Background with opacity */}
                    <div style={{
                        position: 'absolute',
                        inset: '0',
                        borderRadius: '50%',
                        backgroundColor: 'var(--primary-color)',
                        opacity: 0.2
                    }} />
                    {/* Icon without opacity */}
                    <svg
                        style={{
                            width: '32px',
                            height: '32px',
                            color: 'var(--text-brand)'
                        }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                    </svg>
                </div>
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: 'var(--text-brand)',
                        marginBottom: '4px'
                    }}>
                        {overlayTitle || "Drop Files Anywhere to Upload"}
                    </div>
                    <div style={{
                        fontSize: '14px',
                        color: 'var(--text-secondary)'
                    }}>
                        {overlayDescription || `${getFileTypeDescription()} (${getFileSizeDescription()}) • Upload starts immediately`}
                    </div>
                </div>
            </div>
        </div>
    );

    // Create overlay using portal to document.body
    useEffect(() => {
        if (isDragOver && !disabled && !isUploading) {
            const overlay = document.createElement('div');
            overlay.className = `upload-everywhere-overlay ${overlayMode ? 'overlay-mode' : ''} ${overlayClassName}`;
            
            overlay.style.cssText = `
                position: fixed !important;
                top: 0 !important;
                left: ${overlayMode ? '50%' : '0'} !important;
                right: ${overlayMode ? 'auto' : '0'} !important;
                bottom: 0 !important;
                width: 100vw !important;
                height: 100vh !important;
                z-index: 999999 !important;
                border: 3px dashed #8740FF !important;
                display: flex !important;
                flex-direction: column !important;
                align-items: center !important;
                justify-content: center !important;
                pointer-events: none !important;
                box-sizing: border-box !important;
                margin: 0 !important;
                padding: 0 !important;
                ${overlayMode ? 'transform: translateX(-50%) !important;' : ''}
            `;

            // Background
            const background = document.createElement('div');
            background.style.cssText = `
                position: absolute !important;
                top: 0 !important;
                left: 0 !important;
                right: 0 !important;
                bottom: 0 !important;
                width: 100% !important;
                height: 100% !important;
                background-color: #8740FF !important;
                opacity: 0.15 !important;
                pointer-events: none !important;
            `;

            // Content
            const content = document.createElement('div');
            content.style.cssText = `
                pointer-events: none !important;
                z-index: 1 !important;
                position: relative !important;
                background: var(--surface-card) !important;
                padding: 24px !important;
                border-radius: 8px !important;
                box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
                border: 1px solid #8740FF !important;
            `;
            content.innerHTML = `
                <div style="display: flex; flex-direction: column; align-items: center; gap: 16px;">
                    <div style="width: 64px; height: 64px; border-radius: 50%; display: flex; align-items: center; justify-content: center; position: relative;">
                        <div style="position: absolute; inset: 0; border-radius: 50%; background-color: #8740FF; opacity: 0.2;"></div>
                        <svg style="width: 32px; height: 32px; color: #8740FF;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                        </svg>
                    </div>
                    <div style="text-align: center;">
                        <div style="font-size: 18px; font-weight: 600; color: #8740FF; margin-bottom: 4px;">
                            ${overlayTitle || "Drop Files Anywhere to Upload"}
                        </div>
                        <div style="font-size: 14px; color: var(--text-secondary);">
                            PDF, PNG, JPG, XML files up to 50MB each • Upload starts immediately
                        </div>
                    </div>
                </div>
            `;

            overlay.appendChild(background);
            overlay.appendChild(content);
            document.body.appendChild(overlay);

            return () => {
                if (document.body.contains(overlay)) {
                    document.body.removeChild(overlay);
                }
            };
        }
    }, [isDragOver, disabled, isUploading, overlayClassName, overlayTitle]);

    return null;
};

export default UploadEverywhere;
